import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  FileText,
  Scale,
  Clock
} from 'lucide-react';

import SeoMeta from './SeoMeta';
const TermsOfService: React.FC = () => {
  const sections = [
    {
      icon: <FileText className="w-5 h-5" />,
      title: "1. Acceptance of Terms",
      content: `By accessing or using Indian Stock Market GPT ("Service", "Platform", "we/us/our"), you acknowledge that you have read, understood, and agree to be legally bound by this Agreement. If you do not accept every term herein, you must refrain from using the Service immediately.`
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "2. Permitted Purpose",
      content: `The Service is an AI-powered educational tool intended exclusively to:

a. Provide general, methodological insight into Indian equity markets;

b. Demonstrate practical applications of artificial-intelligence in market research; and

c. Support self-directed learning by retail participants.

You may not employ the Service for any unlawful, unauthorized, or commercial activity beyond the scope expressly permitted herein.`
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "3. No Human Investment Advice – AI-Generated Content Only",
      content: `Non-Advisory Status. We are not registered investment advisers under SEBI or any other regulatory body.

AI Outputs. Recommendations, forecasts, or analytical commentary are autogenerated by an artificial-intelligence model trained on historical data; such content is not vetted, endorsed, or personalised by human personnel.

Educational 1:1 Sessions. Any workshops, webinars, or 1:1 sessions delivered by us are limited to general educational methodology and omit specific securities, charts, or index calls.

Your Responsibility. Past performance is not indicative of future returns. You bear sole liability for investment decisions and agree to consult a qualified, independent financial professional before transacting in securities.`
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "4. Privacy & Data Protection",
      content: `Minimal Collection. We collect only the data strictly necessary to operate and improve the Service (e.g., anonymised chat logs, email address upon opt-in).

Lawful Basis. Processing is conducted under legitimate interest in accordance with the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 and applicable data-protection statutes.

Storage & Security. Data are encrypted in transit and at rest using industry-standard safeguards.

No Sale of Data. We do not sell or rent your personal information to third parties.

Data Subject Rights. You may request access, correction, or deletion of your personal data by emailing privacy@indianIndianStockMarketGPT.com.

Detailed procedures are set out in our full Privacy Policy, incorporated herein by reference.`
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      title: "5. User Obligations",
      content: `By using the Service you agree to:

a. Abstain from attempting to manipulate, reverse-engineer, or overload the AI system;

b. Refrain from disseminating unlawful, defamatory, or infringing content;

c. Verify AI-generated information through independent sources prior to reliance;

d. Respect all intellectual-property rights vested in us or our licensors; and

e. Not redistribute, scrape, or commercialise outputs without express written consent.`
    },
    {
      icon: <Scale className="w-5 h-5" />,
      title: "6. Intellectual Property",
      content: `All source code, algorithms, design elements, trademarks, and written materials published on the Service are our exclusive property or that of our licensors and are protected under applicable copyright and trademark laws. No licence is granted except the limited, revocable, non-transferable right to access and use the Service for personal educational purposes.`
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      title: "7. Limitation of Liability",
      content: `To the maximum extent permitted by law, we, our affiliates, officers, employees, and agents shall not be liable for any direct, indirect, incidental, consequential, punitive, or exemplary damages (including, without limitation, loss of profits, trading losses, data, goodwill, or other intangible losses) arising out of or in connection with:

a. Your use of, or inability to use, the Service;

b. Reliance on AI-generated content;

c. Service outages, errors, or inaccuracies; or

d. Third-party content or services accessed through the Platform.

Your sole and exclusive remedy is to discontinue use of the Service.`
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "8. Service Availability & Modifications",
      content: `We strive for continuous availability but provide the Service "as-is" and "as-available." We reserve the unrestricted right to modify, suspend, or discontinue any part of the Service, including paid features, without notice. We assume no liability for any disruption or loss incurred thereby.`
    },
    {
      icon: <Scale className="w-5 h-5" />,
      title: "9. Governing Law & Jurisdiction",
      content: `This Agreement shall be governed by and construed in accordance with the laws of the Republic of India. All disputes arising hereunder shall be submitted to the exclusive jurisdiction of the competent courts at Hyderabad, Telangana, India.`
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "10. Contact",
      content: `Questions or concerns? Please email legal@indianIndianStockMarketGPT.com.`
    }
  ];

  const lastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SeoMeta title="Terms of Service | IndianStockMarketGPT" description="Terms governing use of IndianStockMarketGPT, an educational AI research site for Indian stock market investors. Not investment advice." url="https://indianstockmarketgpt.com/terms" />
      {/* Header */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 border border-navy-100 bg-navy-50 rounded-full px-4 py-1.5 mb-8">
              <Scale className="w-3.5 h-3.5 text-navy-500" />
              <span className="text-xs font-medium text-navy-600 tracking-wide uppercase">Legal Terms & Conditions</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold font-serif mb-6 text-slate-900 leading-tight">
              Terms of <span className="text-navy-500">Service</span>
            </h1>

            <p className="text-lg text-slate-500 mb-8 leading-relaxed max-w-xl mx-auto">
              Please read these terms carefully before using Indian Stock Market GPT
            </p>

            <div className="inline-flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-4 py-2">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-sm text-slate-500">Last updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer banner */}
      <section className="px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4 bg-amber-50 border border-amber-200 rounded-xl p-5">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-700 mb-1.5">Important Disclaimer</h3>
              <p className="text-sm text-amber-700/80 leading-relaxed">
                This platform is powered by AI and does not offer any investment tips or advisory services.
                All content is AI-generated and educational in nature. No human recommendations are provided.
                Please consult a licensed financial advisor before making any investment decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms sections */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {sections.map((section, index) => (
            <div
              key={index}
              className="bg-white border border-slate-200 rounded-xl p-7 hover:border-navy-200 hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-navy-50 border border-navy-100 text-navy-500 flex-shrink-0">
                  {section.icon}
                </div>
                <h2 className="text-lg font-semibold text-slate-800 font-serif">{section.title}</h2>
              </div>
              <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line pl-1">
                {section.content}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA footer */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm">
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-3">
              Questions About These Terms?
            </h2>
            <p className="text-slate-500 mb-8 max-w-md mx-auto leading-relaxed">
              If you have any questions about these Terms of Service, please contact us.
              We're here to help clarify any concerns you may have.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-navy-500 hover:bg-navy-600 text-white font-semibold px-7 py-3 rounded-lg transition-colors text-sm"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
